import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PB_FIELDS = [["total", "Pending Billing Value (INR Cr)"]];
const OV_FIELDS = [
  ["booking_ytd_target", "Booking YTD Target (INR Cr)"], ["booking_ytd_achieved", "Booking YTD Achieved (INR Cr)"],
  ["booking_month_target", "Booking Month Target (INR Cr)"], ["booking_month_achieved", "Booking Month Achieved (INR Cr)"],
  ["billing_ytd_target", "Billing YTD Target (INR Cr)"], ["billing_ytd_achieved", "Billing YTD Achieved (INR Cr)"],
  ["billing_month_target", "Billing Month Target (INR Cr)"], ["billing_month_achieved", "Billing Month Achieved (INR Cr)"],
];
const FO_FIELDS = [
  ["zones", "Zones"], ["branches", "Branches"], ["segments", "Segments"],
  ["product_types", "Product Types"], ["executives", "Executives"],
];
const COM_FIELDS = [
  ["dso", "DSO (days)"], ["coll_bal", "Collection Balance"], ["gt180", ">180 days"],
  ["gt120", ">120 days"], ["gt90", ">90 days"], ["gt60", ">60 days"], ["gt30", ">30 days"], ["lt30", "<30 days"],
];

function NumGrid({ fields, values, onChange, prefix }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {fields.map(([k, label]) => (
        <div key={k} className="space-y-1">
          <Label className="text-xs text-slate-500">{label}</Label>
          <Input type="number" value={values?.[k] ?? ""} className="h-9"
            onChange={(e) => onChange(k, e.target.value === "" ? null : Number(e.target.value))}
            data-testid={`${prefix}-${k}`} />
        </div>
      ))}
    </div>
  );
}

export default function SalesBIData() {
  const qc = useQueryClient();
  const [pb, setPb] = useState({});
  const [com, setCom] = useState({});
  const [comp, setComp] = useState([]);
  const [ov, setOv] = useState({});
  const [fo, setFo] = useState({});
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["sales-bi-manual"],
    queryFn: async () => (await api.get("/sales-bi/manual")).data,
  });
  useEffect(() => {
    if (data) {
      setPb(data.pending_billing || {});
      setCom(data.commercials || {});
      setComp(data.competition || []);
      setOv(data.overrides || {});
      const f = data.filter_options || {};
      setFo(Object.fromEntries(Object.entries(f).map(([k, v]) => [k, Array.isArray(v) ? v.join(", ") : v || ""])));
    }
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const hasPb = Object.values(pb).some((v) => v != null && v !== "");
      const hasCom = Object.values(com).some((v) => v != null && v !== "");
      const filterOptions = {};
      for (const [k] of FO_FIELDS) {
        const list = (fo[k] || "").split(",").map((s) => s.trim()).filter(Boolean);
        if (list.length) filterOptions[k] = list;
      }
      const overrides = Object.fromEntries(Object.entries(ov).filter(([, v]) => v != null && v !== ""));
      await api.put("/sales-bi/manual", {
        pending_billing: hasPb ? pb : {},
        commercials: hasCom ? com : {},
        competition: comp.filter((r) => (r.competitor || "").trim()),
        filter_options: filterOptions,
        overrides,
      });
      toast.success("Sales BI data saved — dashboard updated");
      qc.invalidateQueries({ queryKey: ["sales-bi"] });
      qc.invalidateQueries({ queryKey: ["sales-bi-manual"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div>
      <PageHeader title="Sales BI Data" subtitle="Manually maintained numbers shown on the Sales Dashboard — pending billing status, commercials (DSO / receivables aging) and the competition landscape.">
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="bi-data-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save All
        </Button>
      </PageHeader>

      <div className="space-y-4 max-w-4xl">
        <Card className="p-5">
          <p className="font-heading font-semibold text-slate-900 mb-1">Pending Billing Status</p>
          <p className="text-xs text-slate-500 mb-4">Single pending-billing value shown on the dashboard (INR Cr). Leave blank to show "No data".</p>
          <NumGrid fields={PB_FIELDS} values={pb} onChange={(k, v) => setPb((p) => ({ ...p, [k]: v }))} prefix="pb" />
        </Card>

        <Card className="p-5">
          <p className="font-heading font-semibold text-slate-900 mb-1">Dashboard Filter Options</p>
          <p className="text-xs text-slate-500 mb-4">Comma-separated lists shown in the dashboard filter dropdowns. Leave a field blank to use defaults.</p>
          <div className="space-y-3">
            {FO_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs text-slate-500">{label}</Label>
                <Input value={fo[k] || ""} placeholder="e.g. North, South, West, Central" className="h-9"
                  onChange={(e) => setFo((p) => ({ ...p, [k]: e.target.value }))} data-testid={`fo-${k}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <p className="font-heading font-semibold text-slate-900 mb-1">Manual Number Overrides</p>
          <p className="text-xs text-slate-500 mb-4">Manually entered numbers replace the auto-computed Booking/Billing figures on the unfiltered dashboard view. Blank = automatic.</p>
          <NumGrid fields={OV_FIELDS} values={ov} onChange={(k, v) => setOv((p) => ({ ...p, [k]: v }))} prefix="ov" />
        </Card>

        <Card className="p-5">
          <p className="font-heading font-semibold text-slate-900 mb-1">Commercials — DSO &amp; Receivables Aging</p>
          <p className="text-xs text-slate-500 mb-4">Fallback only — upload the SalesData export in ERP Sync Log → "Upload Receivables" for automatic DSO &amp; aging. Enter amounts in INR.</p>
          <NumGrid fields={COM_FIELDS} values={com} onChange={(k, v) => setCom((p) => ({ ...p, [k]: v }))} prefix="com" />
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-heading font-semibold text-slate-900">Competition Landscape</p>
              <p className="text-xs text-slate-500">Competitor intel shown in the dashboard's Competition Landscape dialog.</p>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setComp((p) => [...p, { competitor: "", region: "", product: "", remarks: "" }])} data-testid="comp-add-row">
              <Plus className="h-4 w-4" /> Add Row
            </Button>
          </div>
          {comp.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No competitor entries yet.</p> : (
            <div className="space-y-2">
              {comp.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`comp-row-${i}`}>
                  <Input placeholder="Competitor *" value={r.competitor || ""} className="h-9 flex-1 min-w-[140px]"
                    onChange={(e) => setComp((p) => p.map((x, j) => j === i ? { ...x, competitor: e.target.value } : x))} data-testid={`comp-name-${i}`} />
                  <Input placeholder="Region" value={r.region || ""} className="h-9 w-28"
                    onChange={(e) => setComp((p) => p.map((x, j) => j === i ? { ...x, region: e.target.value } : x))} />
                  <Input placeholder="Product" value={r.product || ""} className="h-9 w-32"
                    onChange={(e) => setComp((p) => p.map((x, j) => j === i ? { ...x, product: e.target.value } : x))} />
                  <Input placeholder="Remarks" value={r.remarks || ""} className="h-9 flex-1 min-w-[160px]"
                    onChange={(e) => setComp((p) => p.map((x, j) => j === i ? { ...x, remarks: e.target.value } : x))} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600"
                    onClick={() => setComp((p) => p.filter((_, j) => j !== i))} data-testid={`comp-del-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
